import { X } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import type { EmailSendModalProps } from './types';
import { translations } from './constants';
import { useEmailSend } from './useEmailSend';
import { StatusMessages } from './StatusMessages';
import { RegisteredEmailOption, NewEmailOption } from './EmailOptionButton';
import { EmailSendActions } from './EmailSendActions';

export function EmailSendModal({
  isOpen,
  onClose,
  onSendEmail,
  registeredEmail,
}: EmailSendModalProps) {
  const { locale } = useLanguage();
  const t = translations[locale];

  const {
    selectedOption,
    newEmail,
    isValidEmail,
    isSending,
    sendStatus,
    inputRef,
    canSend,
    handleEmailChange,
    handleSelectRegistered,
    handleSelectNew,
    handleSend,
    handleClose
  } = useEmailSend({ onClose, onSendEmail, registeredEmail });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-background rounded-lg shadow-xl animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-bold text-foreground uppercase">
            {t.title}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSending}
            className="p-1 hover:bg-muted rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <StatusMessages sendStatus={sendStatus} t={t} />

          {/* Option Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              {t.selectOption}
            </label>

            <RegisteredEmailOption
              isSelected={selectedOption === 'registered'}
              registeredEmail={registeredEmail}
              isSending={isSending}
              t={t}
              onSelect={handleSelectRegistered}
            />

            <NewEmailOption
              isSelected={selectedOption === 'new'}
              email={newEmail}
              isValidEmail={isValidEmail}
              isSending={isSending}
              t={t}
              inputRef={inputRef as React.RefObject<HTMLInputElement>}
              onSelect={handleSelectNew}
              onEmailChange={handleEmailChange}
            />
          </div>
        </div>

        {/* Footer */}
        <EmailSendActions
          isSending={isSending}
          canSend={canSend}
          t={t}
          onCancel={handleClose}
          onSend={handleSend}
        />
      </div>
    </div>
  );
}
